import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import store from "./redux/store";
import App from "./App";
import { isAuthenticated, resolveAndApplyUserLocale } from "./i18n";
import "./index.css";
import "@svar-ui/react-gantt/all.css";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import Notification from "components/Elements/Notification";
import PseudoLocaleToggle from "components/Elements/PseudoLocaleToggle";
import ErrorBoundary from "components/Layouts/ErrorBoundaryWrapper/ErrorBoundary";
import { GoogleOAuthProvider } from "@react-oauth/google";
import installResizeObserverLoopFix from "helpers/resizeObserverLoopFix";

installResizeObserverLoopFix();

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30_000,
			gcTime: 5 * 60_000,
			retry: 1,
			refetchOnWindowFocus: false,
		},
	},
});

// Configuration object constructed.
const config = {
	auth: {
		clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID,
	},
};

// create PublicClientApplication instance
const publicClientApplication = new PublicClientApplication(config);
publicClientApplication.initialize();

const renderApp = () => {
	ReactDOM.createRoot(document.getElementById("root")).render(
		<MsalProvider instance={publicClientApplication}>
			<GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
				<Provider store={store}>
					<QueryClientProvider client={queryClient}>
						<ErrorBoundary>
							<App />
							<Notification />
							<PseudoLocaleToggle />
						</ErrorBoundary>
						{process.env.NODE_ENV === "development" && (
							<ReactQueryDevtools initialIsOpen={false} />
						)}
					</QueryClientProvider>
				</Provider>
			</GoogleOAuthProvider>
		</MsalProvider>
	);
};

// When signed in, resolve the effective locale for the current site app (the
// user's preferred language if the site app offers it, otherwise the site-app
// default) and apply it — localising the stored captions — before the first
// render. Pre-auth (login etc.) stays English and needs no pre-step.
if (isAuthenticated()) {
	resolveAndApplyUserLocale({ reload: false }).finally(renderApp);
} else {
	renderApp();
}
